import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Loader2, GraduationCap, BookMarked, ClipboardCheck } from 'lucide-react';
import { api } from '../../api/client';

/**
 * Chat "Entrenar AI" por pregunta (coach Sonnet 5).
 * El coach explica/mejora la respuesta y destila reglas al cuaderno FAQ que el
 * motor usa en TODAS las respuestas futuras. Si el coach propone una respuesta
 * (prefijo "RESPUESTA PROPUESTA:"), el botón "Usar como respuesta" la copia al
 * casillero de la card.
 */

interface CoachMsg {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  saved_faq?: { id: string; accion: string; text: string }[] | null;
}

const PROPOSAL_RE = /RESPUESTA PROPUESTA:\s*([\s\S]+)/;

export function extractProposal(text: string): string | null {
  const m = text.match(PROPOSAL_RE);
  if (!m) return null;
  return m[1].split('\n')[0].trim() || null;
}

export function QuestionCoachDrawer({
  sellerId,
  questionId,
  questionText,
  itemTitle,
  itemId,
  siteId,
  aiDraft,
  onUseAnswer,
  onClose,
}: {
  sellerId: number;
  questionId: number;
  questionText: string;
  itemTitle: string;
  itemId: string;
  siteId: string;
  aiDraft: string | null;
  onUseAnswer: (text: string) => void;
  onClose: () => void;
}) {
  const [messages, setMessages] = useState<CoachMsg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await api.get(
        `/mercadolibre/questions/${questionId}/coach?seller_id=${sellerId}`
      );
      if (res.success) setMessages(res.data.messages || []);
    } catch {
      /* historial vacío no bloquea */
    } finally {
      setLoading(false);
    }
  }, [questionId, sellerId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setSending(true);
    setError(null);
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setInput('');
    try {
      const res: any = await api.post(
        `/mercadolibre/questions/${questionId}/coach?seller_id=${sellerId}`,
        {
          message: msg,
          question_text: questionText,
          item_title: itemTitle,
          item_id: itemId,
          site_id: siteId,
          ai_draft: aiDraft,
        }
      );
      if (res.success) {
        setMessages((m) => [
          ...m,
          { role: 'assistant', content: res.data.reply, saved_faq: res.data.saved },
        ]);
      } else {
        setError(res.error || 'No se pudo responder');
      }
    } catch (err: any) {
      setError(err?.message || 'Error de red');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-md h-full bg-[var(--color-bg)] border-l border-[var(--color-border)] shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <GraduationCap size={18} className="text-violet-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-bold text-[var(--color-text)]">Entrenar AI</div>
              <div className="text-[10px] text-[var(--color-text-muted)] truncate" title={questionText}>
                &ldquo;{questionText}&rdquo;
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
            <X size={18} />
          </button>
        </div>

        {/* Mensajes */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)] py-4">
              <Loader2 size={14} className="animate-spin" /> Cargando conversación…
            </div>
          ) : messages.length === 0 ? (
            <div className="text-xs text-[var(--color-text-muted)] py-6 text-center space-y-2">
              <p>Charlá con la AI sobre esta pregunta y su respuesta.</p>
              <p>
                Lo que le enseñes acá queda anotado en el cuaderno de FAQ y el motor lo aplica
                en <strong>todas</strong> las respuestas futuras.
              </p>
            </div>
          ) : (
            messages.map((m, i) => {
              const proposal = m.role === 'assistant' ? extractProposal(m.content) : null;
              return (
                <div key={m.id ?? `local-${i}`} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                  <div
                    className={`max-w-[88%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                      m.role === 'user'
                        ? 'bg-violet-600 text-white'
                        : 'bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text)]'
                    }`}
                  >
                    {m.content}
                    {(m.saved_faq?.length ?? 0) > 0 && (
                      <div className="mt-2 space-y-1">
                        {m.saved_faq!.map((s, j) => (
                          <div
                            key={j}
                            className="flex items-start gap-1.5 text-[11px] px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/25 text-emerald-500"
                          >
                            <BookMarked size={12} className="shrink-0 mt-0.5" />
                            <span>
                              FAQ {s.accion} ({s.id}): {s.text}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {proposal && (
                      <button
                        onClick={() => onUseAnswer(proposal)}
                        className="mt-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-500/15 text-green-500 text-xs font-medium hover:bg-green-500/25"
                      >
                        <ClipboardCheck size={13} /> Usar como respuesta
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
          {sending && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
              <Loader2 size={13} className="animate-spin" /> Pensando…
            </div>
          )}
          {error && <div className="text-xs text-red-400">{error}</div>}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-[var(--color-border)] flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder='Ej: "Esta respuesta está mal porque…" o "Cuando pregunten X, respondé así…"'
            className="flex-1 resize-none px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]"
          />
          <button
            onClick={send}
            disabled={!input.trim() || sending}
            className="h-10 w-10 flex items-center justify-center rounded-lg bg-violet-600 text-white hover:opacity-90 disabled:opacity-50 shrink-0"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
